import { PerspectiveCamera, type Scene } from "three";
import type { Engine } from "../../engine/Engine";
import type { GTAOAccumulatePostProcessElement } from "./elements/GTAOAccumulatePostProcessElement";
import type { SSGIPostProcessElement } from "./elements/SSGIPostProcessElement";
import type { SugarTAAPostProcessElement } from "./elements/SugarTAAPostProcessElement";
import type { SugarPathTracer } from "./SugarPathTracer";
import { loadEnvironmentTexture } from "./loadEnvironmentTexture";
import { renderSettingsStore } from "./RenderSettingsStore";

const appliedEnvUrlByScene = new WeakMap<Scene, string>();

/**
 * Load `renderSettingsStore.environmentUrl` onto the active scene.
 */
export async function applyEnvironmentFromSettings(
  engine: Engine,
): Promise<void> {
  const scene = engine.activeScene;
  if (!scene) return;

  const url = renderSettingsStore.value.environmentUrl;
  if (!url) return;

  if (appliedEnvUrlByScene.get(scene) === url && scene.environment) {
    renderSettingsStore.applyScene(scene);
    return;
  }

  const texture = await loadEnvironmentTexture(url);
  if (!texture || engine.activeScene !== scene) return;

  scene.environment = texture;
  appliedEnvUrlByScene.set(scene, url);
  renderSettingsStore.applyScene(scene);
  engine.dispatchCustomEvent("webgl-render-scene-changed");
  engine.dispatchCustomEvent("request-update");
}

/**
 * Push store into renderer/scene/env + postprocess Lit elements under `host`.
 * Missing elements / entities are skipped (re-applied on `entity-created`).
 */
export function applyRenderSettingsToHost(
  host: ParentNode,
  engine: Engine,
  options?: { applyEnvironment?: boolean },
): void {
  if (!engine.activeScene) return;

  const settings = renderSettingsStore.value;
  const renderer = engine.renderer.instance;
  const prevPixelRatio =
    typeof renderer.getPixelRatio === "function"
      ? renderer.getPixelRatio()
      : undefined;
  const prevSamples = Reflect.get(renderer, "_samples");
  renderSettingsStore.applyRenderer(renderer as never);
  renderSettingsStore.applyScene(engine.activeScene);
  if (prevPixelRatio !== renderSettingsStore.resolvePixelRatio()) {
    engine.resize(true);
  }
  if (prevSamples !== Reflect.get(renderer, "_samples")) {
    engine.invalidateTslEffects();
  }

  const ssgi = host.querySelector(
    "ssgi-postprocess",
  ) as SSGIPostProcessElement | null;
  const gtao = host.querySelector(
    "gtao-accumulate-postprocess",
  ) as GTAOAccumulatePostProcessElement | null;
  const taa = host.querySelector(
    "sugar-taa",
  ) as SugarTAAPostProcessElement | null;
  const pathTracer = host.querySelector(
    "sugar-path-tracer",
  ) as SugarPathTracer | null;

  // 2D (ortho / top camera): TAA only. SSGI / GTAO are 3D.
  const cam = engine.activeCamera?.instance;
  const is3d = cam instanceof PerspectiveCamera;
  const pathTraceOn = settings.pathTrace.enabled;
  const ssgiOn = !!ssgi && settings.ssgi.enabled && !pathTraceOn && is3d;
  const ssgiSparseOn =
    !!ssgi &&
    settings.ssgiSparse.enabled &&
    !pathTraceOn &&
    !ssgiOn &&
    is3d;
  const gtaoOn =
    !!gtao &&
    settings.gtao.enabled &&
    !pathTraceOn &&
    !ssgiOn &&
    !ssgiSparseOn &&
    is3d;

  if (ssgi?.entity) {
    ssgi.applySettings({ ...settings.ssgi, enabled: ssgiOn });
    ssgi.applySparseSettings({
      ...settings.ssgiSparse,
      enabled: ssgiSparseOn,
    });
  }

  if (gtao?.entity) {
    gtao.applySettings({ ...settings.gtao, enabled: gtaoOn });
  }

  if (taa?.entity) {
    // SSGI temporal filtering already wraps TRAA on the composite (official
    // three.js chain). A second TRAA on sugar-taa double-jitters the camera
    // and prevents history from converging.
    // Sparse GI v2 has its own GI history (no TRAA), so sugar-taa stays free.
    const ssgiOwnsTraa =
      (ssgiOn && settings.ssgi.useTemporalFiltering) ||
      (ssgiSparseOn &&
        settings.ssgiSparse.version !== "v2" &&
        settings.ssgiSparse.useTemporalFiltering);
    // Sparse GI v2 debug views are pixel-exact (moving sample points); TAA would blend them away.
    const sparseV2DebugView =
      ssgiSparseOn &&
      settings.ssgiSparse.version === "v2" &&
      (settings.ssgiSparse.output === "debug" ||
        settings.ssgiSparse.output === "source");
    taa.applySettings({
      ...settings.taa,
      enabled:
        pathTraceOn || ssgiOwnsTraa || sparseV2DebugView
          ? false
          : settings.aa.fxaa || settings.taa.enabled,
    });
  }

  if (pathTracer?.entity) {
    const { enabled, ...options } = settings.pathTrace;
    pathTracer.options = options;
    pathTracer.enabled = enabled;
    const ent = pathTracer.entity as {
      updateOptions?: (o: typeof options) => void;
      setEnabled?: (e: boolean) => void | Promise<void>;
    };
    ent.updateOptions?.(options);
    void ent.setEnabled?.(enabled);
  }

  if (options?.applyEnvironment !== false) {
    void applyEnvironmentFromSettings(engine);
  }
  engine.dispatchCustomEvent("request-update");
}
